import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "../../shared/util/cn";

export interface PaginationProps {
  count: number;
  page: number; // 0-based for display
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  rowsPerPageOptions?: number[];
  labelRowsPerPage?: string;
  labelDisplayedRows?: (params: { from: number; to: number; count: number }) => string;
  className?: string;
}

export const Pagination: React.FC<PaginationProps> = ({
  count,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPageOptions = [5, 10, 25, 50, 100],
  labelRowsPerPage = "Righe per pagina:",
  labelDisplayedRows = ({ from, to, count }) =>
    `${from}-${to} di ${count !== -1 ? count : `more than ${to}`}`,
  className,
}) => {
  const from = count === 0 ? 0 : page * rowsPerPage + 1;
  const to = Math.min(page * rowsPerPage + rowsPerPage, count);
  const totalPages = Math.max(0, Math.ceil(count / rowsPerPage));

  return (
    <div
      className={cn(
        "flex flex-wrap items-center justify-between gap-3 border-t border-[var(--color-mockup-border)] px-4 py-3",
        "text-sm text-[var(--color-mockup-text-mid)]",
        className
      )}
    >
      <div className="flex items-center gap-2">
        <span className="whitespace-nowrap font-medium text-[var(--color-mockup-text)]">
          {labelRowsPerPage}
        </span>
        <select
          value={rowsPerPage}
          onChange={(e) => onRowsPerPageChange(Number(e.target.value))}
          className={cn(
            "h-9 min-w-[4rem] cursor-pointer rounded-2xl border border-[var(--color-mockup-border)] bg-surface px-3 text-sm font-semibold text-[var(--color-mockup-text)]",
            "focus:border-primary focus:outline-none focus:shadow-[0_4px_12px_rgba(18,181,170,0.15)] transition-all duration-200"
          )}
        >
          {rowsPerPageOptions.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
        <span className="whitespace-nowrap">
          {labelDisplayedRows({ from, to, count: count === -1 ? -1 : count })}
        </span>
      </div>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 0}
          className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-full text-[var(--color-mockup-text-mid)] transition-colors hover:bg-primary/10 hover:text-primary disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-inherit"
          aria-label="Pagina precedente"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <button
          type="button"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages - 1 || totalPages === 0}
          className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-full text-[var(--color-mockup-text-mid)] transition-colors hover:bg-primary/10 hover:text-primary disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-inherit"
          aria-label="Pagina successiva"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};
